import { FunctionComponent, useCallback, useId, useMemo } from 'react';
import {
    DEFAULT_RADIUS,
    locationsWithGeoCoordinates,
    LocationWithGeoCoordinates,
} from '../../../services/search/locationFilter';
import Link from 'next/link';
import { WebCourseSearchFilters } from '../../../services/search/course/useCourseSearch';
import { WebEducatorSearchFilters } from '../../../services/search/educator/useEducatorSearch';
import { UrlObject } from '../../../hooks/useRouterState';
import { SearchPageProps } from '../../../pages/courses/[[...parts]]';
import styles from '../../../pages/courses/styles.module.scss';
import { stringify } from 'querystring';

type SearchByLocationProps = {
    searchTerm: string;
    filters: {
        courseFilters: WebCourseSearchFilters;
        educatorFilters: WebEducatorSearchFilters;
    };
    getFilterLink: (newFilters: SearchPageProps['query']['filters']) => UrlObject;
};

const SearchByLocation: FunctionComponent<SearchByLocationProps> = ({
    searchTerm,
    filters,
    getFilterLink,
}) => {
    const id = useId();

    const topLocations: Array<LocationWithGeoCoordinates> = useMemo(() => {
        return [...locationsWithGeoCoordinates.slice(0, 15)];
    }, []);

    const getLocationFilterLink = useCallback(
        (newValue: WebCourseSearchFilters['location']) => {
            return getFilterLink({
                ...filters,
                courseFilters: {
                    ...filters.courseFilters,
                    location: newValue,
                },
                educatorFilters: {
                    ...filters.educatorFilters,
                    location: newValue,
                },
            });
        },
        [filters, getFilterLink]
    );

    return (
        <section>
            <h2 id={`${id}-search-by-location-header`}>Search By Location</h2>
            <ul className={styles.links_grid} aria-labelledby={`${id}-search-by-location-header`}>
                {topLocations.map((location: LocationWithGeoCoordinates, index: number) => {
                    const locationUrl = getLocationFilterLink({
                        known_location: location.name,
                        radius: DEFAULT_RADIUS,
                    });
                    if (locationUrl.query) {
                        delete locationUrl.query['page'];
                    }

                    return (
                        <li
                            className={styles.links_grid_item}
                            key={index}
                            aria-label={location.name}
                        >
                            <Link href={locationUrl}>
                                {searchTerm} Courses in {location.name}
                            </Link>
                        </li>
                    );
                })}
            </ul>
        </section>
    );
};

export default SearchByLocation;
